import type { Extensions } from '@tiptap/core';
import { Image } from '@tiptap/extension-image';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import { Paragraph } from '@tiptap/extension-paragraph';
import { TableKit } from '@tiptap/extension-table';
import { Placeholder } from '@tiptap/extensions';
import { Markdown } from '@tiptap/markdown';
import StarterKit from '@tiptap/starter-kit';
import type { SupportGitHubImagePlacement } from '../../../../models/Support/ISupportGitHub';

/**
 * Ticket images enter the issue body as `support-attachment:{id}` references because their durable
 * public URLs only exist once the API publishes them during issue creation; the API swaps each
 * reference for its URL in place.
 */
const ATTACHMENT_REFERENCE_SCHEME = 'support-attachment:';
// Matches the API's reference pattern exactly, so both sides agree on which images the body uses.
const ATTACHMENT_REFERENCE_REGEX = /\]\(support-attachment:([^)\s]*)\)/g;
const INLINE_ATTACHMENT_REGEX = /!\[[^\]\n]*\]\(support-attachment:([^)\s]*)\)/g;

const buildAttachmentReference = (attachmentId: string) => `${ATTACHMENT_REFERENCE_SCHEME}${attachmentId}`;

const parseAttachmentReference = (href: string | null | undefined): string | null =>
	href?.startsWith(ATTACHMENT_REFERENCE_SCHEME) ? href.slice(ATTACHMENT_REFERENCE_SCHEME.length) : null;

export interface IssueImageReference {
	attachmentId: string;
	placement: SupportGitHubImagePlacement;
}

/** Every ticket image the body references, once each. */
export const getIssueImageReferences = (markdown: string): IssueImageReference[] => {
	const inlineIds = new Set(Array.from(markdown.matchAll(INLINE_ATTACHMENT_REGEX), match => match[1]));
	const referencedIds = new Set(Array.from(markdown.matchAll(ATTACHMENT_REFERENCE_REGEX), match => match[1]));
	return Array.from(referencedIds, attachmentId => ({
		attachmentId,
		placement: inlineIds.has(attachmentId) ? 'Inline' : 'Attachment',
	}));
};

export interface IssueImageSource {
	id: string;
	fileName: string;
	url?: string;
}

export const toMarkdownLabel = (value: string) => value.replace(/[[\]\n]/g, '');

/** Markdown for one ticket image, used while the editor shows markdown source. */
export const buildAttachmentMarkdown = (image: IssueImageSource, placement: SupportGitHubImagePlacement) => {
	const reference = buildAttachmentReference(image.id);
	const label = toMarkdownLabel(image.fileName);
	return placement === 'Inline' ? `![${label}](${reference})` : `[📎 ${label}](${reference})`;
};

export interface IssueMarkdownOptions {
	/** Read on every parse so a markdown-source edit can resolve images loaded after the editor mounted. */
	getImageUrl: (attachmentId: string) => string | undefined;
	placeholder: string;
}

/**
 * The draft comes from customer text and AI output, so an outside image URL in it would be fetched from
 * the admin's browser, telling that host who opened the ticket and when. Only images Levitate hosts are
 * previewed; GitHub proxies the rest once the issue exists.
 */
const isPreviewableImageUrl = (src: unknown): boolean => {
	if (typeof src !== 'string' || !src) return false;
	try {
		const url = new URL(src, window.location.href);
		if (url.origin === window.location.origin) return true;
		return url.protocol === 'https:' && (url.hostname === 'levitate.ai' || url.hostname.endsWith('.levitate.ai'));
	} catch {
		return false;
	}
};

/**
 * The editor shows the ticket's display URL while `attachmentId` keeps the reference, so the markdown
 * written back carries `support-attachment:{id}` rather than a temporary, expiring link.
 */
const createIssueImage = (getImageUrl: IssueMarkdownOptions['getImageUrl']) =>
	Image.extend({
		// useImageDrag moves images; native drag and drop cannot place a drop on these block-rendered inline nodes.
		draggable: false,

		addAttributes() {
			return {
				...this.parent?.(),
				attachmentId: {
					default: null,
					parseHTML: (element: HTMLElement) => element.getAttribute('data-attachment-id'),
					renderHTML: (attributes: Record<string, unknown>) =>
						attributes.attachmentId ? { 'data-attachment-id': attributes.attachmentId } : {},
				},
			};
		},

		renderHTML({ node, HTMLAttributes }) {
			if (node.attrs.attachmentId || isPreviewableImageUrl(node.attrs.src)) {
				return ['img', HTMLAttributes];
			}
			return [
				'img',
				{ ...HTMLAttributes, src: '', 'data-external-src': node.attrs.src, title: 'Outside image, not previewed' },
			];
		},

		parseMarkdown: (token, helpers) => {
			const attachmentId = parseAttachmentReference(token.href);
			return helpers.createNode('image', {
				src: attachmentId ? (getImageUrl(attachmentId) ?? '') : token.href,
				alt: token.text,
				title: token.title,
				attachmentId,
			});
		},

		renderMarkdown: node => {
			const attachmentId = node.attrs?.attachmentId as string | null | undefined;
			const src = attachmentId ? buildAttachmentReference(attachmentId) : (node.attrs?.src ?? '');
			const alt = toMarkdownLabel(String(node.attrs?.alt ?? ''));
			const title = node.attrs?.title ? ` "${String(node.attrs.title).replace(/"/g, '')}"` : '';
			return `![${alt}](${src}${title})`;
		},
	}).configure({ inline: true, allowBase64: false });

/**
 * The stock paragraph lifts a lone image out of its paragraph, assuming block images. These images are
 * inline, so that leaves an inline node at block level: an invalid document on which every later
 * transaction throws, including selecting the image.
 */
const IssueParagraph = Paragraph.extend({
	parseMarkdown: (token, helpers) => {
		const tokens = token.tokens ?? [];
		if (tokens.length === 1 && tokens[0].type === 'image') {
			return helpers.createNode('paragraph', undefined, helpers.parseInline(tokens));
		}
		return Paragraph.config.parseMarkdown?.(token, helpers) ?? [];
	},
});

export const createIssueMarkdownExtensions = ({ getImageUrl, placeholder }: IssueMarkdownOptions): Extensions => [
	StarterKit.configure({
		// Markdown has no underline, so an underline mark would be dropped silently on save.
		underline: false,
		paragraph: false,
		link: {
			openOnClick: false,
			autolink: true,
			defaultProtocol: 'https',
			isAllowedUri: (url, context) => url.startsWith(ATTACHMENT_REFERENCE_SCHEME) || context.defaultValidate(url),
		},
	}),
	IssueParagraph,
	createIssueImage(getImageUrl),
	TaskList,
	TaskItem.configure({ nested: true }),
	TableKit.configure({ table: { resizable: false } }),
	Placeholder.configure({ placeholder }),
	Markdown.configure({ markedOptions: { gfm: true } }),
];
